import { Controller } from "@hotwired/stimulus"
import { formatClock, readDelays, totalMs } from "../modules/terminal_reveal"

// The REVEAL terminal: an asciinema-style replay with an HONEST clock. The
// recording is server-rendered in full (.term-lines > .tl spans, each carrying
// data-ms — its real delay in the condensed replay) and the plain-text transcript
// sits in <details class="transcript" open> below: crawlable and complete with JS
// off. On connect this controller closes the transcript (the bay is now the
// transcript) and arms the transport — a Replay latch, a 1×/2× speed seg, a
// progress trace and a mono clock counting actual elapsed replay time.
//
// Honest instrument rules: replay halts off-screen (IntersectionObserver) and on
// hidden tabs; reduced motion gets no rAF timing at all — Replay stays a real
// control that re-renders the recording instantly.
//
// This is the REVEAL model (reveal server-rendered spans), NOT the cast-inject
// player in replayable_terminal_controller — JS-off == JS-on is the law here.
//
// Audio (Monitor Bus tick on play/speed change) is intentionally omitted.
//
// Controller element: the terminal <figure>.
export default class extends Controller {
  connect() {
    this.box = this.element.querySelector(".term-lines")
    this.play = this.element.querySelector("[data-term-play]")
    if (!this.box || !this.play) return
    this.lbl = this.play.querySelector("[data-term-lbl]")
    this.led = this.play.querySelector(".led")
    this.bar = this.element.querySelector(".term-progress i")
    this.clock = this.element.querySelector(".term-clock")
    this.details = this.element.querySelector("details.transcript")

    this.details?.removeAttribute("open") // JS present: the bay is the transcript

    this.lines = Array.from(this.box.querySelectorAll(".tl"))
    this.delays = readDelays(this.lines)
    this.total = totalMs(this.delays)

    this.speed = 1
    this.playing = false
    this.shown = this.lines.length
    this.elapsed = this.total
    this.lineAt = 0
    this.last = 0
    this.raf = 0
    this.visible = true
    this.done = true

    this.cursor = document.createElement("span")
    this.cursor.className = "cursor blink"

    this._onSeg = this._onSeg.bind(this)
    this.seg = this.element.querySelector("[data-term-speeds]")
    this.seg?.addEventListener("click", this._onSeg)

    if (this._reducedMotion) {
      // Reduced motion means no unsolicited motion, not no capability: Replay
      // re-renders the full recording instantly — state stepping, no rAF timing.
      this._showAllStatic()
      this.play.title = "Reduced motion — replay renders the full recording instantly"
      this._onPlayRM = () => this._showAllStatic()
      this.play.addEventListener("click", this._onPlayRM)
      return
    }

    // At rest the recording shows in full — pressing Replay re-runs it.
    if (this.bar) this.bar.style.width = "100%"
    if (this.clock) this.clock.textContent = formatClock(this.total)

    this._onPlay = this._onPlay.bind(this)
    this._frame = this._frame.bind(this)
    this.play.addEventListener("click", this._onPlay)

    if (typeof IntersectionObserver === "function") {
      this.io = new IntersectionObserver(
        (es) => es.forEach((e) => { this.visible = e.isIntersecting; this._rearm() }),
        { threshold: 0.1 }
      )
      this.io.observe(this.element)
    }
    this._onVis = () => { if (!document.hidden) this._rearm() }
    document.addEventListener("visibilitychange", this._onVis)
  }

  disconnect() {
    cancelAnimationFrame(this.raf)
    this.io?.disconnect()
    if (this._onVis) document.removeEventListener("visibilitychange", this._onVis)
    if (this._onPlay) this.play.removeEventListener("click", this._onPlay)
    if (this._onPlayRM) this.play.removeEventListener("click", this._onPlayRM)
    this.seg?.removeEventListener("click", this._onSeg)
  }

  get _reducedMotion() {
    return typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  _render() {
    this.lines.forEach((l, i) => l.classList.toggle("hid", i >= this.shown))
    this.cursor.remove()
    if (this.playing && this.shown < this.lines.length) {
      const anchor = this.shown ? this.lines[this.shown - 1] : this.box
      anchor.insertAdjacentElement(this.shown ? "afterend" : "afterbegin", this.cursor)
    }
  }

  _showAllStatic() {
    this.shown = this.lines.length
    this.playing = false
    this._render()
    if (this.bar) this.bar.style.width = "100%"
    if (this.clock) this.clock.textContent = formatClock(this.total)
  }

  _onSeg(e) {
    const b = e.target.closest("[data-speed]")
    if (!b) return
    this.speed = +b.dataset.speed
    this.seg.querySelectorAll("[data-speed]").forEach((x) => x.setAttribute("aria-pressed", String(x === b)))
  }

  _stop(atEnd) {
    this.playing = false
    cancelAnimationFrame(this.raf)
    this.play.setAttribute("aria-pressed", "false")
    this.led?.classList.remove("on")
    if (atEnd) {
      this.done = true
      if (this.lbl) this.lbl.textContent = "Replay"
      if (this.bar) this.bar.style.width = "100%"
      if (this.clock) this.clock.textContent = formatClock(this.total)
    } else if (this.lbl) {
      this.lbl.textContent = "Play"
    }
    this._render()
  }

  _frame(now) {
    if (!this.playing) return
    if (!this.visible || document.hidden) { this.raf = 0; return } // truly pause; re-armed on re-entry
    const dt = (now - this.last) * this.speed
    this.elapsed += dt
    this.lineAt += dt
    while (this.shown < this.lines.length && this.lineAt >= this.delays[this.shown]) {
      this.lineAt -= this.delays[this.shown]
      this.shown++
      this._render()
    }
    if (this.bar) this.bar.style.width = Math.min(100, (this.elapsed / this.total) * 100) + "%"
    if (this.clock) this.clock.textContent = formatClock(Math.min(this.elapsed, this.total))
    if (this.shown >= this.lines.length) return this._stop(true)
    this.last = now
    this.raf = requestAnimationFrame(this._frame)
  }

  // Re-arm from off-screen/hidden — reset `last` so elapsed doesn't jump.
  _rearm() {
    if (this.playing && !this.raf && this.visible && !document.hidden) {
      this.last = performance.now()
      this.raf = requestAnimationFrame(this._frame)
    }
  }

  _onPlay() {
    if (this.done) { this.done = false; this.shown = 0; this.elapsed = 0; this.lineAt = 0 }
    this.playing = !this.playing
    this.play.setAttribute("aria-pressed", String(this.playing))
    this.led?.classList.toggle("on", this.playing)
    if (this.lbl) this.lbl.textContent = this.playing ? "Pause" : "Play"
    if (this.playing) {
      this.last = performance.now()
      this._render()
      this.raf = requestAnimationFrame(this._frame)
    } else {
      this._stop(false)
    }
  }
}
